import { Injectable } from '@angular/core';
import {
  IPaginatedResult,
  ISapDataService,
  ISort,
  mapODataFilters,
  ODataFilter,
  rethrowAsSapError,
} from 'src/app/lego-utils';
import { drop, take } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Task } from 'src/app/models/domain';
import { TaskAdapter } from 'src/app/models/domain/adapters';
import { ProjectTaskDataService } from 'src/app/services/project-task-data.service';
import { ProjectSetService, TaskSetService } from 'src/generated-clients/project';

@Injectable({
  providedIn: 'root',
})
export class TaskDataService implements ISapDataService<Task, string> {
  public constructor(
    private readonly taskService: TaskSetService,
    private readonly projectDataService: ProjectTaskDataService,
    private readonly projectService: ProjectSetService,
    private readonly taskAdapter: TaskAdapter
  ) {}

  public update(id: string, entity: Partial<Task>): Observable<any> {
    const compositeId = id.split(';');
    const projectGuid = compositeId[0];
    const taskGuid = compositeId[1];

    const dto = this.taskAdapter.adaptToDTO(entity);

    return this.projectDataService.updateTask(dto, projectGuid, taskGuid).pipe(rethrowAsSapError());
  }

  public getSingle(id: string): Observable<Task> {
    const [projectGuid, taskGuid] = id.split(';');

    return this.taskService
      .taskSetProjectGuidprojectGuidtaskGuidtaskGuidGet(`guid'${projectGuid}'`, `guid'${taskGuid}'`)
      .pipe(
        rethrowAsSapError(),
        map((task) => {
          if (!task.d) {
            throw new Error('task not found');
          }

          return this.taskAdapter.adaptToDomain(task.d);
        })
      );
  }

  public getMultiple(filters?: ODataFilter): Observable<Task[]> {
    return this.projectService
      .projectSetGet(mapODataFilters(filters, '2'), undefined, undefined, ['toTask'])
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          const projects = response.d?.results;

          if (projects === undefined) {
            throw new Error('There was no results array!');
          }

          const tasks: Task[] = [];

          for (const project of projects) {
            const taskResults = project.toTask?.results;

            if (taskResults === undefined) {
              continue;
            }

            tasks.push(...taskResults.map((task) => this.taskAdapter.adaptToDomain(task)));
          }

          return tasks;
        })
      );
  }

  public getPage(
    page: number,
    perPage: number,
    _sort?: Array<ISort<Task>>,
    filters?: ODataFilter,
    _expand?: string[]
  ): Observable<IPaginatedResult<Task>> {
    return this.projectService
      .projectSetGet(mapODataFilters(filters, '2'), 'allpages', undefined, ['toTask'])
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          const projects = response.d?.results;

          if (projects === undefined) {
            throw new Error('There was no results array!');
          }

          const tasks: Task[] = [];

          for (const project of projects) {
            const tasksResults = project.toTask?.results;

            if (tasksResults === undefined) {
              continue;
            }

            tasks.push(...tasksResults.map((task) => this.taskAdapter.adaptToDomain(task)));
          }

          return {
            currentPage: page,
            data: take(drop(tasks, (page - 1) * perPage), perPage),
            perPage,
            total: tasks.length,
          };
        })
      );
  }

  public create(_entity: Partial<Task>): Observable<Task> {
    throw new Error('Method not implemented.');
  }

  public delete(_id: string, _options?: unknown): Observable<void> {
    throw new Error('Method not implemented.');
  }
}
